import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { ROUTES } from '../../app/routes';
import { requestChatPrefill } from './chatPrefill';
import { subscribeToKanbanUpdates } from '../../services/communicationService';
import { useInsuredDetail } from '../../context/InsuredDetailContext';
import { fetchWidgetContent, sandboxFromManifest, type WidgetManifest, type WidgetSurface } from '../../services/myToolsApi';
import { CONNECT_RATE_LIMIT, performConnectionCall, performScopedFetch, RateLimiter, type WidgetConnectRequest, type WidgetFetchRequest } from './widgetDataAccess';
import { currentUserKey, handleStorageRequest, localStorageBackend, memoryBackend, type StorageOp, type WidgetStorageRequest } from './widgetStorage';
import { readDesignTokens, readLocaleContext } from './widgetTheme';
import { grantedNetworkOrigins, withHostPolicy } from './widgetHostPolicy';
import '../../styles/Widgets.css';

// Reject documents too small to be a real widget (mirrors MacAIChatWidget) or too
// large to hold in memory as srcDoc.
const MIN_HTML_LENGTH = 100;
const MAX_HTML_BYTES = 5_000_000;
const MIN_FRAME_HEIGHT = 120;
const DEFAULT_MAX_FRAME_HEIGHT = 4000;

// Tokens that would let widget code escape the sandbox. Stripped even if a server
// response ever contained them — allow-same-origin in particular would hand the
// widget the host's localStorage (and its auth token).
const NEVER_ALLOWED = new Set([
  'allow-same-origin',
  'allow-top-navigation',
  'allow-top-navigation-by-user-activation',
  'allow-popups-to-escape-sandbox',
  'allow-downloads',
  'allow-storage-access-by-user-activation',
  'allow-pointer-lock',
  'allow-presentation',
]);

export interface WidgetFrameProps {
  /** Installed/visible widget to load from the API. */
  toolId?: string;
  /** This install's row id — required for momentum:connect (a connection's secret is stored
   *  per installation). Omit for previews: connect calls fail closed with no installation to look up. */
  installationId?: string;
  /** Pre-built document (local preview before save). Takes precedence over toolId. */
  html?: string;
  manifest?: WidgetManifest | null;
  /** Server-computed sandbox tokens; falls back to a client-side mapping of the manifest. */
  grantedSandbox?: string[];
  /** Install-time config values delivered to the widget via momentum:init. */
  config?: Record<string, string>;
  surface?: WidgetSurface;
  height?: number | '100%';
  autoResize?: boolean;
  maxHeight?: number;
  title?: string;
  className?: string;
  onResize?: (height: number) => void;
  onReady?: () => void;
  /** Installer paused this widget: nothing is fetched or run. */
  paused?: boolean;
  pausedHint?: string;
  /** Keep Momentum.storage in memory for this frame only (reviewer/marketplace previews), so a
   *  preview can't read or change the data the person's real install keeps. */
  ephemeralStorage?: boolean;
}

type BridgeMessage = {
  type?: unknown; height?: unknown; url?: unknown; text?: unknown; op?: unknown; key?: unknown; value?: unknown;
  message?: unknown; source?: unknown; line?: unknown; path?: unknown; level?: unknown; connection?: unknown;
  channel?: unknown;
} & Partial<Omit<WidgetFetchRequest, 'id'>> & { id?: unknown };

const NOTIFY_MIN_INTERVAL_MS = 3000;
const MAX_NOTIFY_CHARS = 200;

// In-app routes only: must start with a single "/", no scheme, no whitespace, never the auth pages.
function isSafeInternalPath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0];
  return path.length > 0 && path.length <= 500
    && /^\/(?!\/)[^\s<>"'\\]*$/.test(path)
    // "/./login" or "/x/../login" would normalise past the check below.
    && !pathname.split('/').some(seg => seg === '.' || seg === '..')
    && !/^\/(login|logout|signup|register|reset-password)(\/|\?|#|$)/i.test(path);
}

/** True when the person has just interacted (a click in the widget counts: activation reaches
 *  the host page). Browsers without the API are treated as active; the throttles still apply. */
function hasUserActivation(): boolean {
  const ua = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return ua ? ua.isActive : true;
}
const CLIPBOARD_MIN_INTERVAL_MS = 1000;
const PREFILL_MIN_INTERVAL_MS = 3000;

/** Uncaught error reported by the widget's SDK (`momentum:error`). */
export interface WidgetRuntimeError { message: string; source: string; line: number; at: number }
const MAX_STORED_ERRORS = 20;
// See momentum:resize — more growing reports than this within the window is a feedback loop.
const RUNAWAY_MAX_STEPS = 40;
const RUNAWAY_WINDOW_MS = 2000;

function hasPermission(manifest: WidgetManifest | null | undefined, permission: string): boolean {
  return (manifest?.permissions ?? []).some(p => p === permission);
}

const WidgetFrameInner: React.FC<WidgetFrameProps> = ({
  toolId, installationId, html, manifest, grantedSandbox, config, surface = 'page',
  height = 480, autoResize = true, maxHeight = DEFAULT_MAX_FRAME_HEIGHT,
  title, className, onResize, onReady, paused = false, pausedHint, ephemeralStorage = false,
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { selectedInsuredId } = useInsuredDetail();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const lastNotifyRef = useRef(0);
  const lastClipboardRef = useRef(0);
  const lastPrefillRef = useRef(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const limiterRef = useRef(new RateLimiter());
  const connectLimiterRef = useRef(new RateLimiter(CONNECT_RATE_LIMIT.max, CONNECT_RATE_LIMIT.windowMs));
  // momentum:resize can arrive far faster than the screen can redraw (a buggy or hostile
  // widget in a resize loop); coalesce to at most one state update per animation frame
  // instead of one per message, so a flood costs one re-render, not thousands.
  const pendingHeightRef = useRef<number | null>(null);
  const growthRef = useRef({ last: 0, streak: 0, since: 0 });
  const resizeRafRef = useRef<number | null>(null);
  // momentum:storage shares the fetch RateLimiter's shape but its own budget — a runaway
  // `for(;;) Momentum.storage.set(...)` would otherwise re-serialise and write the whole
  // per-widget store on every tick.
  const storageLimiterRef = useRef(new RateLimiter(120, 1000));
  // Host-mediated real-time: the host holds the one real SignalR connection (via the same
  // factory Command Center/Communication Center already use) and forwards a bare "changed"
  // ping — the widget never gets a connection of its own. Only opened once the widget's own
  // code actually calls Momentum.subscribe('kanban', ...), and only when granted.
  const kanbanUnsubscribeRef = useRef<(() => void) | null>(null);
  // Pre-save previews have no tool id yet: their storage lives only as long as the frame.
  const memoryStoreRef = useRef<Record<string, unknown>>({});
  const storageBackend = useMemo(
    () => (toolId && !ephemeralStorage ? localStorageBackend(currentUserKey(), toolId) : memoryBackend(memoryStoreRef)),
    [toolId, ephemeralStorage],
  );

  const [srcDoc, setSrcDoc] = useState<string | null>(html ?? null);
  const [loading, setLoading] = useState(!html && !!toolId);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [frameHeight, setFrameHeight] = useState<number | null>(null);
  // Uncaught errors inside the widget, surfaced as a small badge so a broken widget is
  // visible (to the person and to its author) instead of silently blank.
  const [runtimeErrors, setRuntimeErrors] = useState<WidgetRuntimeError[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  // What actually goes into the frame: the document plus the host policy (widgetHostPolicy.ts) —
  // only the document's own <script> elements may run, and, when a manifest is supplied (the
  // installer's effective one, or a reviewer preview's), network access is limited to its grants.
  // Built asynchronously (script hashing); nothing renders until it's ready, and if it can't be
  // built the widget doesn't run at all.
  const networkOriginsKey = manifest ? grantedNetworkOrigins(manifest.permissions).join(' ') : null;
  const [frameDoc, setFrameDoc] = useState<string | null>(null);
  useEffect(() => {
    setFrameDoc(null);
    if (!srcDoc) return;
    let cancelled = false;
    const origins = networkOriginsKey === null ? null : (networkOriginsKey ? networkOriginsKey.split(' ') : []);
    withHostPolicy(srcDoc, origins)
      .then(doc => { if (!cancelled) setFrameDoc(doc); })
      .catch(() => { if (!cancelled) setError("This widget couldn't be prepared to run safely in this browser."); });
    return () => { cancelled = true; };
  }, [srcDoc, networkOriginsKey]);

  // One document = one load event. A second load means the widget navigated its frame to
  // another page (location.href, form post, meta refresh) — the one thing neither the sandbox
  // nor the CSP can prevent — so the frame is killed instead of showing an arbitrary page.
  const loadCountRef = useRef(0);
  // One nonce per document handed to the frame. The SDK echoes it in momentum:unload when its
  // page is unloaded — which happens *before* a page the widget navigated to can run anything —
  // so the bridge is shut before that page could use the widget's grants. Unload messages from a
  // document the host replaced itself carry an old nonce and are ignored.
  const docNonce = useMemo(
    () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [frameDoc, reloadKey],
  );
  const escapedRef = useRef(false);
  const initialisedRef = useRef(false);
  useEffect(() => {
    loadCountRef.current = 0;
    escapedRef.current = false;
    initialisedRef.current = false;
  }, [frameDoc, reloadKey]);

  const stopEscapedFrame = useCallback(() => {
    if (escapedRef.current) return;
    escapedRef.current = true;
    // eslint-disable-next-line no-console
    console.warn(`[widget${manifest?.name ? ` ${manifest.name}` : ''}] navigated its frame away from the sandboxed document — stopped`);
    setRuntimeErrors(prev => [...prev.slice(-(MAX_STORED_ERRORS - 1)), {
      message: 'Widget tried to navigate its frame to another page and was stopped', source: '', line: 0, at: Date.now(),
    }]);
    setSrcDoc(null);
    setError('This widget tried to leave its sandbox by navigating to another page, so it was stopped. Reload to try again, and let the widget’s author know.');
  }, [manifest?.name]);

  const sandbox = useMemo(() => {
    const tokens = (grantedSandbox && grantedSandbox.length > 0 ? grantedSandbox : sandboxFromManifest(manifest))
      .filter(t => !NEVER_ALLOWED.has(t));
    if (!tokens.includes('allow-scripts')) tokens.unshift('allow-scripts');
    return tokens.join(' ');
  }, [grantedSandbox, manifest]);

  useEffect(() => {
    if (html != null) {
      setSrcDoc(html);
      setLoading(false);
      setError(null);
      return;
    }
    if (!toolId) return;
    if (paused) {
      setSrcDoc(null);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setRuntimeErrors([]);
    setShowErrors(false);
    fetchWidgetContent(toolId, controller.signal)
      .then(doc => {
        if (controller.signal.aborted) return;
        if (!doc || doc.length < MIN_HTML_LENGTH) throw new Error('This widget has no content yet.');
        if (doc.length > MAX_HTML_BYTES) throw new Error('This widget is too large to display.');
        setSrcDoc(doc);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setSrcDoc(null);
        setError(err instanceof Error ? err.message : 'Failed to load widget');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
  }, [toolId, html, reloadKey, paused]);

  const postToWidget = useCallback((message: Record<string, unknown>) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    // A srcDoc sandboxed frame has an opaque origin, so '*' is the only usable target.
    // Nothing sensitive is ever sent: see momentum:init below.
    win.postMessage({ protocol: 1, ...message }, '*');
  }, []);

  // What the user is looking at — lets a widget act on the current insured/route
  // without ever seeing ids it wasn't shown in the UI anyway.
  const selection = useMemo(() => ({ insuredId: selectedInsuredId ?? null }), [selectedInsuredId]);
  const route = location.pathname;
  // Read at send time rather than captured: route/selection changes go out incrementally as
  // momentum:context (below), and must not trigger a whole new init — widgets run their
  // on('init') setup each time. Config is compared by value; callers often pass a new object.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const routeRef = useRef(route);
  routeRef.current = route;
  const configRef = useRef(config);
  configRef.current = config;
  const configKey = JSON.stringify(config ?? {});

  const postInit = useCallback(() => {
    postToWidget({
      type: 'momentum:init',
      nonce: docNonce,
      context: {
        protocol: 1,
        widget: { name: manifest?.name ?? '', version: manifest?.version ?? '' },
        user: { name: user?.name ?? '' },
        agency: { name: localStorage.getItem('agency') ?? '' },
        theme,
        // Live design tokens + the viewer's locale, so widgets can match the host and
        // format dates/currency correctly without hard-coding either.
        tokens: readDesignTokens(),
        ...readLocaleContext(),
        config: configRef.current ?? {},
        surface,
        selection: selectionRef.current,
        route: routeRef.current,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postToWidget, docNonce, manifest?.name, manifest?.version, user?.name, theme, configKey, surface]);

  // Widgets keep their own copy of the theme; push changes as they happen.
  useEffect(() => {
    if (initialisedRef.current) postInit();
  }, [theme, postInit]);

  // Selection/route changes are pushed incrementally so widgets can react without a re-init.
  useEffect(() => {
    if (initialisedRef.current) postToWidget({ type: 'momentum:context', context: { selection, route } });
  }, [selection, route, postToWidget]);

  // Everything the message handler reads that can change between renders. The handler is
  // registered once per frame instead of being torn down on every route/prop change — which used
  // to drop the kanban subscription after the first navigation (the SDK never re-subscribes).
  const latestRef = useRef({
    manifest, autoResize, height, maxHeight, onReady, onResize, postInit, storageBackend,
    navigate, showToast, pathname: location.pathname, toolId, installationId, docNonce, stopEscapedFrame,
  });
  latestRef.current = {
    manifest, autoResize, height, maxHeight, onReady, onResize, postInit, storageBackend,
    navigate, showToast, pathname: location.pathname, toolId, installationId, docNonce, stopEscapedFrame,
  };

  // A revoked realtime grant (or a manifest without it) ends the subscription right away.
  useEffect(() => {
    if (!hasPermission(manifest, 'realtime:kanban') && kanbanUnsubscribeRef.current) {
      kanbanUnsubscribeRef.current();
      kanbanUnsubscribeRef.current = null;
    }
  }, [manifest]);

  useEffect(() => {
    const handler = (event: MessageEvent<BridgeMessage>) => {
      // Origin is "null" for srcDoc sandboxed frames — the frame's window identity is the
      // only trustworthy check. Anything else (DevTools, other frames) is ignored.
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      // Once the frame has left its document, nothing it sends is honoured.
      if (escapedRef.current) return;
      const data = event.data;
      if (!data || typeof data.type !== 'string') return;
      const {
        manifest, autoResize, height, maxHeight, onReady, onResize, postInit, storageBackend,
        navigate, showToast, pathname, toolId, installationId, docNonce, stopEscapedFrame,
      } = latestRef.current;
      const clamp = (h: number) => Math.max(manifest?.minHeight ?? MIN_FRAME_HEIGHT, Math.min(maxHeight, h));

      switch (data.type) {
        case 'momentum:unload':
          if ((data as { nonce?: unknown }).nonce === docNonce) stopEscapedFrame();
          break;
        case 'momentum:ready':
          initialisedRef.current = true;
          postInit();
          onReady?.();
          break;
        case 'momentum:resize': {
          if (!autoResize || height === '100%') break;
          const next = Number(data.height);
          if (!Number.isFinite(next) || next <= 0) break;
          // Runaway guard: a body tied to the viewport (min-height:100vh + a margin) reports a
          // bigger height after every resize, climbing to maxHeight one frame at a time. Widgets
          // built before the SDK measured content still do, so stop growing after a long streak.
          const growth = growthRef.current;
          const now = performance.now();
          if (next > growth.last && now - growth.since < RUNAWAY_WINDOW_MS) {
            growth.streak += 1;
          } else {
            growth.streak = next > growth.last ? 1 : 0;
            growth.since = now;
          }
          growth.last = next;
          if (growth.streak > RUNAWAY_MAX_STEPS) break;
          pendingHeightRef.current = clamp(Math.ceil(next));
          if (resizeRafRef.current == null) {
            resizeRafRef.current = requestAnimationFrame(() => {
              resizeRafRef.current = null;
              const clamped = pendingHeightRef.current;
              pendingHeightRef.current = null;
              if (clamped == null) return;
              setFrameHeight(clamped);
              onResize?.(clamped);
            });
          }
          break;
        }
        case 'momentum:open': {
          if (!hasPermission(manifest, 'open-links')) break;
          const url = typeof data.url === 'string' ? data.url.trim() : '';
          if (!/^https:\/\//i.test(url)) break;
          window.open(url, '_blank', 'noopener,noreferrer');
          break;
        }
        case 'momentum:clipboard': {
          // Every outcome is answered: Momentum.copy() returns a promise that settles on this
          // reply, so a refused copy rejects instead of looking done. Older SDKs send no id.
          const reply = (ok: boolean, error?: string) => {
            if (typeof data.id === 'string') postToWidget({ type: 'momentum:clipboard:result', id: data.id, ok, error });
          };
          if (!hasPermission(manifest, 'clipboard-write')) {
            reply(false, "This widget isn't allowed to copy to your clipboard — it can be allowed under Installed → Configuration.");
            break;
          }
          // Only right after the person clicked in the widget, as the permission text promises —
          // not whenever the widget likes (e.g. swapping what they just copied).
          if (!hasUserActivation()) {
            reply(false, 'Copying needs a click in the widget first.');
            break;
          }
          const now = Date.now();
          if (now - lastClipboardRef.current < CLIPBOARD_MIN_INTERVAL_MS) {
            reply(false, 'Copied too quickly — try again in a moment.');
            break;
          }
          lastClipboardRef.current = now;
          const text = typeof data.text === 'string' ? data.text : '';
          if (!text || !navigator.clipboard) {
            reply(false, text ? "Copying isn't available in this browser." : 'Nothing to copy.');
            break;
          }
          navigator.clipboard.writeText(text).then(() => reply(true), () => reply(false, "Couldn't copy to your clipboard."));
          break;
        }
        case 'momentum:fetch': {
          // Scoped data access: validated + rate-limited in widgetDataAccess; the reply
          // carries data or a reason, never the host's credentials.
          const request: WidgetFetchRequest = {
            id: String(data.id ?? ''), scope: String(data.scope ?? ''), path: String(data.path ?? ''),
            method: typeof data.method === 'string' ? data.method : 'GET', query: data.query ?? null, body: data.body,
          };
          performScopedFetch(manifest, request, limiterRef.current)
            .then(result => postToWidget({ type: 'momentum:fetch:result', ...result }))
            .catch(() => postToWidget({ type: 'momentum:fetch:result', id: request.id, ok: false, error: 'Request failed' }));
          break;
        }
        case 'momentum:connect': {
          // Authenticated call to a third-party API the widget declared in manifest.connections.
          // The widget only ever supplies path/method/query/body; the destination and the secret
          // are both resolved server-side (see widgetDataAccess.performConnectionCall) and never
          // reach this frame's own code, let alone the widget's.
          if (!toolId) {
            // A preview (wizard, review) has no installation to connect through. Say so now
            // rather than leaving the SDK to time out 20 s later with a vague error.
            postToWidget({ type: 'momentum:connect:result', id: String(data.id ?? ''), ok: false, status: 412, error: 'Previews cannot connect to other services — install the widget to use Momentum.connect.' });
            break;
          }
          const request: WidgetConnectRequest = {
            id: String(data.id ?? ''), connection: String(data.connection ?? ''), path: typeof data.path === 'string' ? data.path : '',
            method: typeof data.method === 'string' ? data.method : 'GET', query: (data.query as Record<string, string> | null) ?? null, body: data.body,
          };
          performConnectionCall(toolId, installationId, manifest, request, connectLimiterRef.current)
            .then(result => postToWidget({ type: 'momentum:connect:result', ...result }))
            .catch(() => postToWidget({ type: 'momentum:connect:result', id: request.id, ok: false, error: 'Request failed' }));
          break;
        }
        case 'momentum:storage': {
          // Per-widget key/value store, namespaced to this user + widget (see widgetStorage).
          const request: WidgetStorageRequest = {
            id: String(data.id ?? ''), op: String(data.op ?? '') as StorageOp, key: data.key, value: data.value,
          };
          const result = storageLimiterRef.current.take()
            ? handleStorageRequest(storageBackend, request)
            : { id: request.id, ok: false as const, error: 'Too many storage calls — slow down.' };
          postToWidget({ type: 'momentum:storage:result', ...result });
          break;
        }
        case 'momentum:navigate': {
          // In-app navigation only (e.g. to an insured). External links go through momentum:open.
          if (!hasPermission(manifest, 'ui:navigate')) break;
          const path = typeof data.path === 'string' ? data.path.trim() : '';
          if (!isSafeInternalPath(path)) break;
          navigate(path);
          break;
        }
        case 'momentum:notify': {
          if (!hasPermission(manifest, 'ui:notify')) break;
          const now = Date.now();
          if (now - lastNotifyRef.current < NOTIFY_MIN_INTERVAL_MS) break;
          const message = typeof data.message === 'string' ? data.message.trim().slice(0, MAX_NOTIFY_CHARS) : '';
          if (!message) break;
          lastNotifyRef.current = now;
          // The SDK defaults to 'info'; that was shown as a green success toast, so
          // Momentum.notify("Couldn't load data") looked like good news.
          const level = data.level === 'error' ? 'error' : data.level === 'warning' ? 'warning' : data.level === 'success' ? 'success' : 'info';
          showToast(level, manifest?.name ? `${manifest.name}: ${message}` : message);
          break;
        }
        case 'momentum:chat:prefill': {
          // Drafts text into the chat box; the person still has to press send. Only in response
          // to a click in the widget, and throttled — otherwise a sidebar widget could keep
          // dragging the person back to chat. ChatInput never overwrites text they're typing.
          if (!hasPermission(manifest, 'chat:prefill')) break;
          if (!hasUserActivation()) break;
          const now = Date.now();
          if (now - lastPrefillRef.current < PREFILL_MIN_INTERVAL_MS) break;
          const text = typeof data.text === 'string' ? data.text : '';
          if (!text.trim()) break;
          lastPrefillRef.current = now;
          requestChatPrefill(text, manifest?.name);
          const onChat = pathname === ROUTES.HOME || pathname.startsWith(ROUTES.CHAT);
          if (!onChat) navigate(ROUTES.HOME);
          break;
        }
        case 'momentum:subscribe': {
          const channel = typeof data.channel === 'string' ? data.channel : '';
          if (channel === 'kanban' && hasPermission(manifest, 'realtime:kanban') && !kanbanUnsubscribeRef.current) {
            kanbanUnsubscribeRef.current = subscribeToKanbanUpdates(() => {
              // The grant is checked again at delivery time, against the current manifest.
              if (hasPermission(latestRef.current.manifest, 'realtime:kanban')) {
                postToWidget({ type: 'momentum:event', channel: 'kanban', payload: {} });
              }
            });
          }
          break;
        }
        case 'momentum:error': {
          const entry: WidgetRuntimeError = {
            message: typeof data.message === 'string' ? data.message.slice(0, 500) : 'Script error',
            source: typeof data.source === 'string' ? data.source.slice(0, 200) : '',
            line: Number(data.line) || 0,
            at: Date.now(),
          };
          // eslint-disable-next-line no-console
          console.warn(`[widget${manifest?.name ? ` ${manifest.name}` : ''}] ${entry.message}${entry.line ? ` (line ${entry.line})` : ''}`);
          setRuntimeErrors(prev => [...prev.slice(-(MAX_STORED_ERRORS - 1)), entry]);
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      if (resizeRafRef.current != null) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = null;
      pendingHeightRef.current = null;
      if (kanbanUnsubscribeRef.current) {
        kanbanUnsubscribeRef.current();
        kanbanUnsubscribeRef.current = null;
      }
    };
  }, [postToWidget]);

  const handleLoad = useCallback(() => {
    loadCountRef.current += 1;
    // Fallback for widgets whose SDK predates momentum:unload: a second load event means the
    // frame navigated away (it fires after the new page has already started running).
    if (loadCountRef.current > 1) {
      stopEscapedFrame();
      return;
    }
    initialisedRef.current = true;
    postInit();
  }, [postInit, stopEscapedFrame]);

  const fill = height === '100%';
  const resolvedHeight = fill ? undefined : (frameHeight ?? (typeof height === 'number' ? height : 480));
  const frameTitle = title ?? manifest?.name ?? 'Widget';

  return (
    <div
      className={`wg-frame${fill ? ' wg-frame--fill' : ''}${className ? ` ${className}` : ''}`}
      style={fill ? undefined : { height: resolvedHeight }}
    >
      {paused && (
        <div className="wg-frame__state wg-frame__state--paused">
          <span>⏸ Paused — {pausedHint ?? 'resume it from Installed → Configuration.'}</span>
        </div>
      )}
      {frameDoc && !error && !paused && (
        <iframe
          key={reloadKey}
          ref={iframeRef}
          className="wg-frame__iframe"
          title={frameTitle}
          srcDoc={frameDoc}
          sandbox={sandbox}
          referrerPolicy="no-referrer"
          allow=""
          onLoad={handleLoad}
          style={fill ? undefined : { height: resolvedHeight }}
        />
      )}
      {runtimeErrors.length > 0 && !loading && !error && (
        <>
          <button
            type="button"
            className="wg-frame__errors"
            title={`${runtimeErrors.length} error${runtimeErrors.length === 1 ? '' : 's'} inside this widget — click for details`}
            onClick={() => setShowErrors(s => !s)}
          >
            ⚠ {runtimeErrors.length}
          </button>
          {showErrors && (
            <div className="wg-frame__errorlist" role="log">
              <div className="wg-frame__errorlist-head">
                <span>Widget errors (last {Math.min(runtimeErrors.length, 5)})</span>
                <button type="button" className="wg-frame__errorlist-btn" onClick={() => {
                  navigator.clipboard?.writeText(runtimeErrors.map(e => `${new Date(e.at).toISOString()} ${e.message}${e.line ? ` (line ${e.line})` : ''}`).join('\n')).catch(() => {});
                }}>Copy</button>
                <button type="button" className="wg-frame__errorlist-btn" onClick={() => { setRuntimeErrors([]); setShowErrors(false); }}>Clear</button>
              </div>
              {runtimeErrors.slice(-5).reverse().map((e, i) => (
                <div key={`${e.at}-${i}`} className="wg-frame__erroritem">
                  <span>{e.message}</span>
                  {e.line > 0 && <span className="wg-frame__errorloc">line {e.line}</span>}
                </div>
              ))}
              <div className="wg-frame__errorlist-foot">These come from the widget's own code, not from Momentum AMP. Share them with the widget's author.</div>
            </div>
          )}
        </>
      )}
      {loading && (
        <div className="wg-frame__state" aria-busy="true">
          <span>Loading widget…</span>
        </div>
      )}
      {error && !loading && (
        <div className="wg-frame__state wg-frame__state--error" role="alert">
          <span>{error}</span>
          {toolId && (
            <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => setReloadKey(k => k + 1)}>
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface WidgetErrorBoundaryProps { height?: number | '100%'; title?: string; children: React.ReactNode }
interface WidgetErrorBoundaryState { hasError: boolean }

// A widget is third-party code running inside our React tree's iframe host, not the widget's
// own document — a bug in WidgetFrame itself (or a prop shape it didn't expect) throwing during
// render must not take down the surrounding page. This is the only thing standing between one
// broken widget and a blank home screen / sidebar / tools page for every user who has it
// installed, since React has no ancestor boundary anywhere else in the app.
class WidgetErrorBoundary extends React.Component<WidgetErrorBoundaryProps, WidgetErrorBoundaryState> {
  state: WidgetErrorBoundaryState = { hasError: false };
  static getDerivedStateFromError(): WidgetErrorBoundaryState { return { hasError: true }; }
  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[widget${this.props.title ? ` ${this.props.title}` : ''}] crashed while rendering`, error, info.componentStack);
  }
  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    const fill = this.props.height === '100%';
    return (
      <div className="wg-frame" style={fill ? undefined : { height: typeof this.props.height === 'number' ? this.props.height : 160 }}>
        <div className="wg-frame__state wg-frame__state--error" role="alert">
          <span>{this.props.title ? `${this.props.title} ` : 'This widget '}couldn't be shown.</span>
        </div>
      </div>
    );
  }
}

const WidgetFrame: React.FC<WidgetFrameProps> = (props) => (
  <WidgetErrorBoundary height={props.height} title={props.title}>
    <WidgetFrameInner {...props} />
  </WidgetErrorBoundary>
);

export default React.memo(WidgetFrame);
